import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  backendMessage, EmailPreview, EmailViewSwitch, PREVIEW_DELAY_MS, TemplateFields, TemplateProblems,
} from './TemplateParts';
import { saveProblems } from './emailAdminModel';

// One e-mail, edited for one send only (P3b): the registration confirmation and the submission
// decision use it. It opens on the email the person would get now (load(null)), shown as the
// backend's rendering; Edit switches to the {{placeholder}} text and the placeholder list. The
// preview is refreshed after a pause in typing, never per keystroke, so a long text does not fire a
// request per character. Nothing is saved: the text goes back through onConfirm and the backend
// renders it with this person's values, or the saved template is sent when nothing was changed.
//
// load(template|null) answers a RenderedMail ({subject, html, templateProblems, template,
// placeholders}); with `sendsEmail: false` it says this change sends no email at all (the
// submission screen's "no email for this change" case), which allows saving without one.
// A 409 from a preview (nothing to confirm, no address) is shown as its reason, and the button stays
// off. An error from onConfirm stays in the dialog, which stays open.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, .55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1100;
`;

const Panel = styled.div`
  background: #fff;
  max-width: 46rem;
  width: 100%;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, .35);
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1rem;
    max-height: calc(100vh - 1rem);
  }
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: .5rem;
  font-size: 1.15rem;
  margin: 0 0 .75rem;
  color: #0f172a;
  overflow-wrap: anywhere;
`;

const Body = styled.div`flex: 1 1 auto; overflow-y: auto; min-height: 0;`;

const Footer = styled.div`
  display: flex;
  gap: .6rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const DialogButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  padding: 0.6rem 0.9rem;
  font: inherit;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? '#64748b' : '#47ABDB')};

  &:hover:not(:disabled) { background: ${(p) => (p.$secondary ? '#475569' : '#0056b3')}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Note = styled.p`margin: .75rem 0 0; font-size: .8rem; color: #475569; line-height: 1.5;`;

// The admin's edit over the previewed text: only what changed, so a missing part stays the saved
// text. null when nothing changed (then the caller sends today's email unchanged).
export const editedTemplateOf = (subject, html, baseline) => {
  const changed = {};
  if (subject !== baseline.subject) changed.subject = subject;
  if (html !== baseline.html) changed.html = html;
  return Object.keys(changed).length > 0 ? { ...changed, highlight: true } : null;
};

const OneTimeEmailDialog = ({
  title, intro = null, templateKey = null, load, confirmLabel, onConfirm, onClose,
}) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [preview, setPreview] = useState(null);
  const [template, setTemplate] = useState({ subject: '', html: '' });
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [view, setView] = useState('preview');
  const [previewKey, setPreviewKey] = useState(null);
  const [previewing, setPreviewing] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [problems, setProblems] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const seq = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const contentKey = JSON.stringify([subject, html]);
  const upToDate = previewKey === contentKey;
  const nothingToSend = preview?.sendsEmail === false;

  // The person's email as it stands today. load(null) is the saved/default text, no edit.
  useEffect(() => {
    const token = ++seq.current;
    setPreviewing(true);
    load(null)
      .then((data) => {
        if (seq.current !== token) return;
        const text = data.template || { subject: data.subject, html: data.html };
        setPreview(data);
        setTemplate({ subject: text.subject || '', html: text.html || '' });
        setSubject(text.subject || '');
        setHtml(text.html || '');
        setProblems(data.templateProblems || []);
        setPreviewKey(JSON.stringify([text.subject || '', text.html || '']));
        setPreviewing(false);
      })
      .catch((err) => {
        if (seq.current !== token) return;
        setLoadError(backendMessage(err, t('emailAdmin.oneTime.loadFailed', 'Could not load the email.')));
        setPreviewing(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A pause in typing, not every keystroke: the backend renders the draft for this person and
  // reports what would stop it being sent.
  useEffect(() => {
    if (!preview || nothingToSend || previewKey === contentKey) return undefined;
    const sentKey = contentKey;
    const edit = editedTemplateOf(subject, html, template);
    const timer = setTimeout(() => {
      const token = ++seq.current;
      setPreviewing(true);
      loadRef.current(edit)
        .then((data) => {
          if (seq.current !== token) return;
          setPreview(data);
          setProblems(data.templateProblems || []);
          setPreviewKey(sentKey);
          setPreviewError(null);
          setPreviewing(false);
        })
        .catch((err) => {
          if (seq.current !== token) return;
          setPreviewError(backendMessage(err, t('emailAdmin.oneTime.previewFailed', 'Could not prepare the preview.')));
          setPreviewing(false);
        });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [contentKey, preview, nothingToSend, previewKey, subject, html, template]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = useCallback(async () => {
    setSending(true);
    setSendError(null);
    try {
      await onConfirm(nothingToSend ? null : editedTemplateOf(subject, html, template));
      onClose();
    } catch (err) {
      const refused = saveProblems(err);
      if (refused) setProblems(refused);
      else setSendError(backendMessage(err, t('emailAdmin.oneTime.confirmFailed', 'The email was refused.')));
    } finally {
      setSending(false);
    }
  }, [html, nothingToSend, onClose, onConfirm, subject, template]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabled = sending || previewing || !preview || !!loadError || !!previewError
    || (!nothingToSend && (!upToDate || problems.length > 0));

  return (
    <Overlay onClick={sending ? undefined : onClose}>
      <Panel role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <Title><Mail size={18} />{title}</Title>
        <Body>
          {intro && <p style={{ margin: '0 0 .75rem', fontSize: '.88rem', color: '#334155', lineHeight: 1.55 }}>{intro}</p>}
          {loadError && <p role="alert" style={{ margin: '0 0 .75rem', padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b', fontSize: '.82rem', lineHeight: 1.5 }}>{loadError}</p>}
          {sendError && <p role="alert" style={{ margin: '0 0 .75rem', padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b', fontSize: '.82rem', lineHeight: 1.5 }}>{sendError}</p>}

          {!preview && !loadError && (
            <p style={{ margin: '.5rem 0', fontSize: '.82rem', color: '#475569' }}>
              <FormattedMessage id="emailAdmin.oneTime.loading" defaultMessage="Loading the email…" />
            </p>
          )}

          {preview && nothingToSend && (
            <p style={{ margin: '.5rem 0', padding: '.6rem .7rem', background: '#f1f5f9', color: '#334155', fontSize: '.85rem' }}>
              <FormattedMessage id="emailAdmin.oneTime.noEmail"
                defaultMessage="No email will be sent for this change. Saving keeps everything else as it is." />
            </p>
          )}

          {preview && !nothingToSend && (
            <>
              <EmailViewSwitch view={view} onChange={setView} />
              {view === 'edit' ? (
                <TemplateFields subject={subject} html={html} onChangeSubject={setSubject} onChangeHtml={setHtml}
                  placeholders={preview.placeholders || []} />
              ) : (
                <>
                  {previewError && <p role="alert" style={{ margin: '0 0 .75rem', padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b', fontSize: '.82rem' }}>{previewError}</p>}
                  <EmailPreview subject={preview.subject} html={preview.html} loading={previewing} />
                </>
              )}
              <div style={{ marginTop: '.75rem' }}>
                <TemplateProblems problems={problems}
                  titleId="emailAdmin.oneTime.problems" titleDefault="This email can't be sent yet:" />
              </div>
            </>
          )}

          <Note>
            <FormattedMessage id="emailAdmin.oneTime.onlyThisSend"
              defaultMessage="This change is for this email only and is not saved; the saved template stays as it is." />
            {templateKey && (
              <>{' '}
                <Link to={`/admin/emails?tab=emails&key=${templateKey}`} style={{ color: '#0369a1', fontWeight: 700 }}>
                  <FormattedMessage id="emailAdmin.oneTime.openTemplate" defaultMessage="Open the saved template" />
                </Link>
              </>
            )}
          </Note>
        </Body>

        <Footer>
          <DialogButton type="button" $secondary disabled={sending} onClick={onClose}>
            <FormattedMessage id="emailAdmin.oneTime.cancel" defaultMessage="Cancel" />
          </DialogButton>
          <DialogButton type="button" disabled={disabled} onClick={confirm}>
            {sending
              ? <FormattedMessage id="emailAdmin.oneTime.sending" defaultMessage="Saving…" />
              : confirmLabel}
          </DialogButton>
        </Footer>
      </Panel>
    </Overlay>
  );
};

export default OneTimeEmailDialog;
